import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { IPermissions } from 'common/permissions.types';
import { permissionsUtil } from 'utils/permissionsUtil';

import { ProfileData } from 'components/Settings/Settings.types';

import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';

export default function useProfiles(currentPublisherId?: string) {
  const axios = useAxios();
  const { portalId } = useParams();

  const canAccessProfiles = currentPublisherId
    ? !!permissionsUtil.canAccessProfiles(
        localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) || {},
        currentPublisherId
      )
    : false;

  const getProfiles = useQuery<ProfileData[], Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.PROFILES],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.PROFILES),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
    },
    enabled: canAccessProfiles
  });

  const createProfile = useMutation(async (data: Partial<ProfileData>) => {
    return await axios.post(getApiRouteOrMock(EApiRoutes.PROFILES), data);
  }, {});

  const toggleProfile = useMutation(async (data: { isEnabled: boolean }) => {
    return await axios.post(
      getApiRouteOrMock(EApiRoutes.TOGGLE_PROFILES),
      data,
      getCustomHeader(currentPublisherId, portalId)
    );
  }, {});

  const updateProfile = useMutation(async (data: Partial<ProfileData>) => {
    return await axios.put(
      `${getApiRouteOrMock(EApiRoutes.PROFILES)}/${data._id}`,
      data
    );
  }, {});

  const deleteProfile = useMutation(async (profileId: string) => {
    return await axios.del(
      `${getApiRouteOrMock(EApiRoutes.PROFILES)}/${profileId}`
    );
  }, {});

  return {
    getProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    toggleProfile
  };
}
